import React, { useState, useEffect, useRef } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import CustomSnackbar from "../CustomSnackbar";

const libraries = ["places"];
const apiKey = "AIzaSyA8yhA5tQTedFvrKvgYYB_SKEAkKBoZxBM";

const AddressAutocomplete = ({ onChange, defaultValue }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAddress, setCurrentAddress] = useState({
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });
  const [snackbar, setSnackbar] = useState({
    message: "",
    status: "",
    open: false,
  });

  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (defaultValue) {
      setInputValue(defaultValue.address);

      if (typeof defaultValue === "object") {
        setCurrentAddress((prev) => ({
          ...prev,
          street: defaultValue.street || "",
          city: defaultValue.city || "",
          state: defaultValue.state || "",
          zip: defaultValue.zipcode || "",
          country: defaultValue.country || "",
        }));
      } else {
        setCurrentAddress((prev) => ({
          ...prev,
          street: defaultValue,
        }));
      }
    }
  }, [defaultValue]);

  const handleInputChange = (event, value) => {
    setInputValue(value);

    if (value && !options.find((opt) => opt.description === value)) {
      const updatedAddress = {
        ...currentAddress,
        street: value,
      };
      setCurrentAddress(updatedAddress);
      onChange(updatedAddress);
    }

    if (value?.length > 2) {
      setLoading(true);
      try {
        autocompleteService.current.getPlacePredictions(
          { input: value, componentRestrictions: { country: "us" } },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              setOptions(
                predictions.map((prediction) => ({
                  description: prediction.description,
                  place_id: prediction.place_id,
                }))
              );
            } else {
              setOptions([]);
            }
            setLoading(false);
          }
        );
      } catch (error) {
        console.error("Error fetching autocomplete predictions:", error);
        setSnackbar({
          message: "Error fetching address suggestions. Please try again.",
          status: "error",
          open: true,
        });
        setLoading(false);
      }
    } else {
      setOptions([]);
    }
  };

  const handlePlaceSelect = async (event, value) => {
    if (value) {
      const selectedOption = options.find(
        (option) => option.description === value
      );
      if (selectedOption) {
        placesService.current = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
        try {
          placesService.current.getDetails(
            { placeId: selectedOption.place_id },
            (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                const addressComponents = place.address_components;
                const address = {
                  street: "",
                  city: "",
                  state: "",
                  zip: "",
                  country: "",
                };

                addressComponents.forEach((component) => {
                  const types = component.types;
                  if (types.includes("street_number")) {
                    address.street = `${component.long_name} ${address.street}`;
                  }
                  if (types.includes("route")) {
                    address.street += component.long_name;
                  }
                  if (types.includes("locality")) {
                    address.city = component.long_name;
                  }
                  if (types.includes("administrative_area_level_1")) {
                    address.state = component.short_name;
                  }
                  if (types.includes("postal_code")) {
                    address.zip = component.long_name;
                  }
                  if (types.includes("country")) {
                    address.country = component.long_name;
                  }
                });
                setCurrentAddress(address);
                setInputValue(address.street);
                onChange(address);
              } else {
                throw new Error("Failed to fetch place details.");
              }
            }
          );
        } catch (error) {
          console.error("Error fetching place details:", error);
          setSnackbar({
            message: "Error fetching address details. Please try again.",
            status: "error",
            open: true,
          });
        }
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  return (
    <div>
      <Autocomplete
        freeSolo
        options={options.map((option) => option.description)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handlePlaceSelect}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Address"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <CustomSnackbar
        message={snackbar.message}
        status={snackbar.status}
        open={snackbar.open}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default AddressAutocomplete;
